// A list of actions to be shown in a SelectionActionView and acted on by a SelectionStore

import * as _ from "underscore";

import * as utils from "shared/utils";
import SelectionStore from "stores/SelectionStore";

export interface SelectionActionData {
  id: string;
  name?: string;
  namePlural?: string;
  splitter?: boolean;
  singleSelectionOnly?: boolean;
  /** Deletes or removes something: drawn in red */
  destructive?: boolean;
  applyQuery?: Record<string, any>;
  exec?: () => void | Promise<void>;
  [key: string]: any;
}

const itemJSON = (item: any) => {
  if (item?.book?.value) {
    return item.book.value;
  }
  if (item?.state?.value) {
    return item.state.value;
  }
  return item.toJSON();
};

export class SelectionAction {
  readonly data: SelectionActionData;

  constructor(data: SelectionActionData) {
    this.data = data;
  }

  get id() {
    return this.data.id;
  }

  get name() {
    return this.data.name;
  }

  get namePlural() {
    return this.data.namePlural;
  }

  get splitter() {
    return this.data.splitter;
  }

  get singleSelectionOnly() {
    return this.data.singleSelectionOnly;
  }

  get exec() {
    return this.data.exec;
  }

  get viewJSON() {
    return { ...this.data };
  }

  appliesToSelection(
    selectionStore: SelectionStore,
    options: { itemIDsOnly?: boolean } = {}
  ) {
    const applyQuery = this.data.applyQuery;

    // all selected items have to match all conditions to show
    if (
      !options.itemIDsOnly &&
      (selectionStore.wholeCollection.value || selectionStore.wholePage.value)
    ) {
      return this.checkForMatch(
        applyQuery,
        selectionStore.collection.getSelectionQuery?.() ??
          selectionStore.collection.searchQuery?.toJSON?.() ??
          selectionStore.collection.searchQuery?.value
      );
    }

    // fetch all itemIDs - see if ANY of them match
    return _.any(selectionStore.itemIDs.value, (itemID) => {
      const model =
        selectionStore.collection.getWord?.(itemID) ??
        selectionStore.collection.get?.(itemID);
      if (!model) return false;

      // check that every condition matches
      return this.checkForMatch(applyQuery, itemJSON(model));
    });
  }

  appliesToItem(model: any) {
    return this.checkForMatch(this.data.applyQuery, itemJSON(model));
  }

  private checkForMatch(query: Record<string, any> | undefined, json: any) {
    return _.every(query, (value, key) => {
      if (key === "$and") {
        return _.every(value, (condition) =>
          this.checkForMatch(condition, json)
        );
      }
      if (key === "$or") {
        return _.any(value, (condition) => this.checkForMatch(condition, json));
      }
      if (typeof value === "object") {
        return _.every(value, (conditionValue, conditionType) => {
          switch (conditionType) {
            case "$exists":
              return utils.childExists(json, key) === conditionValue;
            case "$ne":
              return !this.checkEqualOrContains(
                utils.valueFromPath(json, key),
                conditionValue
              );
            case "$not": {
              const subQuery = {};
              subQuery[key] = conditionValue;
              return !this.checkForMatch(subQuery, json);
            }
            case "$size": {
              const innerValue = utils.valueFromPath(json, key);
              return innerValue && innerValue.length === conditionValue;
            }
            default:
              throw new Error("condition type not supported: " + conditionType);
          }
        });
      }
      return this.checkEqualOrContains(utils.valueFromPath(json, key), value);
    });
  }

  private checkEqualOrContains(documentValue: any, value: any) {
    if (!documentValue) {
      return false;
    }
    if (documentValue instanceof Array) {
      return _.contains(documentValue, value);
    }
    return documentValue === value;
  }
}

export default class SelectionActionList {
  readonly actions: SelectionAction[];
  readonly selectionStore?: SelectionStore;

  constructor(
    actions: SelectionActionData[],
    options: { selectionStore?: SelectionStore } = {}
  ) {
    this.actions = actions.map((action) => new SelectionAction(action));
    this.selectionStore = options.selectionStore;
  }

  getAction(id: string) {
    return this.actions.find((action) => action.id === id);
  }

  forEach(callback: (action: SelectionAction) => void) {
    this.actions.forEach(callback);
  }

  get viewJSON() {
    return this.actions.map((action) => action.viewJSON);
  }
}
